import { useEffect, useState, useCallback } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import SidebarNav from "./sidebar-nav";
import { getMyCompanies, switchCompany } from "../../api/user";
import { tasksApi } from "../../api/tasks";
import { toast } from "react-hot-toast";
import { DocumentUploadQueueProvider } from "../../contexts/document-upload-queue-context";
import { CompanyAnnouncements } from "./CompanyAnnouncements";
import { FeatureAnnouncementsHost } from "../../featureAnnouncements/FeatureAnnouncementsHost";
import { usePolling } from "../../hooks/usePolling";
import TrainingGate from "./training/TrainingGate";
import HseReadReminder from "./hse/compliance/HseReadReminder";
import {
  CustomerLockTrackerProvider,
  useCustomerLockTracker,
} from "../../contexts/CustomerLockTracker";
import { CustomerLockFloatingNotice } from "./customers/CustomerLockBanner";
import { EmailSignatureSetupBanner } from "./userGuide/EmailSignatureSetupBanner";
import { DashboardTopBar } from "./dashboard-top-bar";
import {
  dashboardDesktopNavMin,
  dashboardDesktopRailClass,
} from "./dashboard-nav-breakpoints";
import { isImmersiveViewerPath } from "./immersive-viewer-layout";
import { useSettings } from "../../contexts/settings-context";
import { useVisualViewportCssVars } from "../../hooks/useVisualViewportCssVars";
import { HomeIcon } from "@heroicons/react/24/outline";
import { Logo } from "../utils/logo";
import "./dashboard-chrome.css";

const TASK_SUMMARY_POLL_MS = 5 * 60 * 1000;

function useWindowSize() {
  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined,
  });

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return windowSize;
}

export const DashboardLayout = () => {
  const { t } = useTranslation();
  const { settings } = useSettings();
  useVisualViewportCssVars();
  const location = useLocation();
  const immersiveViewerOpen = isImmersiveViewerPath(location.pathname);
  const drawingViewerOpen = immersiveViewerOpen;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [taskSummary, setTaskSummary] = useState({
    unfinished: 0,
    due_today: 0,
    overdue: 0,
  });
  const [loadingTasks, setLoadingTasks] = useState(false);

  const windowSize = useWindowSize();
  const currentUser = useSelector((state) => state.session.currentUser);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const loadCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const response = await getMyCompanies();
      if (response.companies) {
        setCompanies(response.companies);
      }
    } catch (error) {
      console.error("Failed to load companies:", error);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const loadTaskSummary = useCallback(async () => {
    setLoadingTasks(true);
    try {
      const response = await tasksApi.getTaskSummary({
        finished: false,
        active: true,
      });
      if (response?.data?.success && response.data.data) {
        const data = response.data.data;
        setTaskSummary({
          unfinished: Number(data.unfinished) || 0,
          due_today: Number(data.due_today) || 0,
          overdue: Number(data.overdue) || 0,
        });
      }
    } catch (error) {
      console.error("Failed to load task summary:", error);
    } finally {
      setLoadingTasks(false);
    }
  }, []);

  useEffect(() => {
    if (currentUser) {
      loadCompanies();
      loadTaskSummary();
    }
  }, [currentUser, loadTaskSummary]);

  usePolling(loadTaskSummary, TASK_SUMMARY_POLL_MS, { enabled: Boolean(currentUser) });

  const currentCompany = companies.find((c) => c.current) || null;

  const handleCompanySwitch = async (company) => {
    if (!company?.id || String(company.id) === String(currentCompany?.id)) {
      return;
    }
    try {
      const response = await switchCompany(company.id);
      if (response.companies) {
        setCompanies(response.companies);
        toast.success(t("dashboard.header.company_switched"));
        window.location.reload();
      }
    } catch (error) {
      toast.error(error?.error || t("dashboard.header.company_switch_failed"));
    }
  };

  const desktopSidebarMin = dashboardDesktopNavMin(immersiveViewerOpen);

  const handleClick = () => {
    if (windowSize.width != null && windowSize.width < desktopSidebarMin) {
      setSidebarOpen(false);
    }
  };

  useEffect(() => {
    if (windowSize.width != null && windowSize.width >= desktopSidebarMin) {
      setSidebarOpen(false);
    }
  }, [desktopSidebarMin, windowSize.width]);

  const desktopContentOffset =
    typeof windowSize.width === "number" && windowSize.width >= desktopSidebarMin
      ? collapsed
        ? "4rem"
        : "16rem"
      : undefined;

  useEffect(() => {
    const sidebarWidth =
      typeof windowSize.width === "number" && windowSize.width >= desktopSidebarMin
        ? collapsed
          ? "4rem"
          : "16rem"
        : "0px";
    document.documentElement.style.setProperty(
      "--dashboard-sidebar-width",
      sidebarWidth
    );
    return () => {
      document.documentElement.style.setProperty("--dashboard-sidebar-width", "0px");
    };
  }, [collapsed, desktopSidebarMin, windowSize.width]);

  useEffect(() => {
    if (!immersiveViewerOpen) return undefined;
    const html = document.documentElement;
    const { body } = document;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    html.classList.add("immersive-viewer-lock");
    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      html.classList.remove("immersive-viewer-lock");
    };
  }, [immersiveViewerOpen]);

  return (
    <div
      className={
        immersiveViewerOpen
          ? "flex h-[100dvh] max-h-[100svh] min-w-0 w-full max-w-full flex-col overflow-hidden overflow-x-hidden"
          : "flex min-h-full flex-col"
      }
    >
      <div
        className={dashboardDesktopRailClass(immersiveViewerOpen)}
        onMouseEnter={() => setCollapsed(false)}
        onMouseLeave={() => setCollapsed(true)}
        style={{ width: collapsed ? "4rem" : "16rem" }}
      >
        <div className="pd-theme pd-sidebar-shell flex flex-grow flex-col overflow-hidden rounded-xl bg-white shadow-xl" data-theme={settings?.theme === "dark" ? "dark" : "light"}>
          <Link to="/dashboard/" className="pd-sidebar-brand" aria-label="Promang" title="Promang">
            {collapsed ? <HomeIcon className="h-6 w-6" aria-hidden="true" /> :
              <Logo color={settings?.theme === "dark" ? "light" : "dark"} sizeClass="h-8" />}
          </Link>
          <div className="mt-2 flex min-h-0 flex-1 flex-col">
            <nav className="flex min-h-0 flex-1 flex-col px-2 pb-4">
              <SidebarNav onClick={handleClick} collapsed={collapsed} />
            </nav>
          </div>
        </div>
      </div>

      <div className="flex min-w-0 max-w-full flex-1 flex-col">
        <DashboardTopBar
          immersiveViewerOpen={immersiveViewerOpen}
          drawingViewerOpen={drawingViewerOpen}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          companies={companies}
          loadingCompanies={loadingCompanies}
          currentCompany={currentCompany}
          onCompanySwitch={handleCompanySwitch}
          taskSummary={taskSummary}
          loadingTasks={loadingTasks}
          currentUser={currentUser}
          onDrawerNavClick={handleClick}
        />

        <DocumentUploadQueueProvider>
          <CustomerLockTrackerProvider>
            <main
              className={`mb-0 flex min-h-0 min-w-0 max-w-full flex-1 flex-col overflow-x-clip lg:transition-[padding-left] lg:duration-300 lg:ease-in-out ${
                immersiveViewerOpen ? "overflow-hidden" : "sm:mb-20"
              }`}
              style={{ paddingLeft: desktopContentOffset }}
            >
              <CompanyAnnouncements />
              <FeatureAnnouncementsHost />
              <HseReadReminder />
              <TrainingGate recoveryActions={<>
                <Link className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold" to="/dashboard/guide">{t("Brukerguide")}</Link>
              </>}>
                <Outlet />
              </TrainingGate>
            </main>
            <CustomerLockTrackerStrip />
            <EmailSignatureSetupBanner />
          </CustomerLockTrackerProvider>
        </DocumentUploadQueueProvider>
      </div>
    </div>
  );
};

function CustomerLockTrackerStrip() {
  const tracker = useCustomerLockTracker();
  return <CustomerLockFloatingNotice customers={tracker?.lockedCustomers || []} />;
}

export default DashboardLayout;
